import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useStateRef from 'react-usestateref';
import { useAccountCardAPI } from '../../apis/account-card-api';
import { usePublicAPI } from '../../apis/public-api';
import { SelectPaymentCardModalBody } from '../../components/payment/select-payment-card-modal-body';
import { SelectPaymentCardModalTitle } from '../../components/payment/select-payment-card-modal-title';
import { useErrorHandler } from '../../hooks/use-error-handler';
import { useFeature } from '../../hooks/use-feature';
import { usePaymentInterval } from '../../hooks/use-payment-interval';
import { useShake } from '../../hooks/use-shake';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { layoutDefaults } from '../../utils/layout-defaults';
import { PaymentType } from '../../utils/manager/card-manager';
import { usePaymentCardManager } from '../../utils/manager/payment-card-manager';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useSelectPaymentCardModal() {
  const storage = useStorage();
  const { setPaying, paymentRef, spot, menuInfo, menuLayout, config } = storage;
  const { generateAndStorePaymentCard, cancelPaymentCard, refreshPaymentCard } =
    usePaymentCardManager();
  const { handleErrorOnModal } = useErrorHandler();
  const confirmButtonTextRef = useRef();
  const analytics = useAnalytics();
  const { analyticsPageView } = analytics;
  const { getPayCardPrivacyPolicyFromRemote } = usePublicAPI();
  const { findAccountCardsFromRemote } = useAccountCardAPI();
  const { swal } = useSwal({ storage });
  const feature = useFeature();
  const { t } = useTranslation();

  const [selectedCard, setSelectedCard, selectedCardRef] = useStateRef();
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const cardsRef = useRef();
  const ordersRef = useRef();
  const tipAmountRef = useRef();
  const cvcRef = useRef();
  const setSelectedErrorRef = useRef();
  const stripePromiseRef = useRef();
  const chargeRef = useRef();
  const isUnderErrorRef = useRef();
  const wasCancelledRef = useRef(false);
  const wasPaidRef = useRef(false);

  const onPaymentPaid = async () => {
    wasPaidRef.current = true;
    swal.close();
  };

  const onPaymentCancelled = async () => {
    wasCancelledRef.current = true;
    swal.close();
  };

  usePaymentInterval(
    {
      payment: paymentRef.current,
      orders: ordersRef.current,
      onPaymentPaid,
      onPaymentCancelled,
    },
    focused && !loading,
    [focused, loading]
  );

  const didOpen = () => {
    analyticsPageView('Select Payment Card');
    setPaying(true);
    setFocused(true);
  };

  const didDestroy = () => {
    setPaying(false);
    setFocused(false);

    setSelectedCard(null);
    setSelectedErrorRef.current = null;
    cvcRef.current = null;
  };

  useShake(
    {
      element: confirmButtonTextRef?.current?.parentElement,
      enabled: selectedCard != null,
    },
    [selectedCard]
  );

  const handleOnCVCCardChange = (cvc) => {
    console.log('handleOnCVCCardChange', cvc);
    cvcRef.current = cvc;
  };

  const handleOnSelectCard = ({ card, setError, charge }) => {
    setSelectedErrorRef.current = setError;
    setSelectedCard(card);
    chargeRef.current = charge;

    swal.resetValidationMessage();
    console.log('handleOnSelectCard', charge);
  };

  async function prepare({ orders, tipAmount }) {
    cardsRef.current = null;
    let hasCards = false;

    try {
      const response = await findAccountCardsFromRemote();
      const cards = response.data;

      ordersRef.current = orders;
      tipAmountRef.current = tipAmount;
      cardsRef.current = cards;
      stripePromiseRef.current = await loadStripe(config?.stripe?.api_key);

      hasCards = !_.isEmpty(cards);

      if (
        hasCards &&
        (paymentRef.current == null ||
          (paymentRef.current === 'paying' &&
            _.isEmpty(paymentRef.current.card)))
      ) {
        await generateAndStorePaymentCard({
          orders,
          tipAmount,
          type: PaymentType.CARD,
        });
      }
    } catch (error) {
      handleErrorOnModal(error);
    }

    return {
      hasCards,
    };
  }

  const handleOnError = (error, handleGeneric) => {
    isUnderErrorRef.current = true;

    if (!_.isEmpty(error)) {
      handleErrorOnModal(error, handleGeneric);
    }
  };

  async function show({ prepareOnPreConfirm, prepareOnPreDeny }) {
    let wasOtherCardClicked = false;
    let wasDeleteCardClicked = false;
    let wasSucccess = false;
    let cardToDelete = null;

    const result = await swal.fire({
      title: (
        <SelectPaymentCardModalTitle
          style={{
            marginTop: layoutDefaults.biggerVerticalMargin,
          }}
          title={t('card-payment.modal.title')}
          analytics={analytics}
          getPrivacyPolicyFromRemote={getPayCardPrivacyPolicyFromRemote}
        />
      ),
      html: (
        <SelectPaymentCardModalBody
          cards={cardsRef.current}
          spot={spot}
          stripePromise={stripePromiseRef.current}
          menuInfo={menuInfo}
          menuLayout={menuLayout}
          loading={loading}
          feature={feature}
          payment={paymentRef.current}
          onSelect={handleOnSelectCard}
          onCVCChange={handleOnCVCCardChange}
          onDelete={(card) => {
            wasDeleteCardClicked = true;
            cardToDelete = card;
            swal.close();
          }}
          onOtherCardClick={() => {
            wasOtherCardClicked = true;
            swal.close();
          }}
          onError={handleOnError}
          onSuccess={() => {
            wasSucccess = true;
          }}
        />
      ),
      showConfirmButton: true,
      showDenyButton: true,
      showLoaderOnConfirm: true,
      showLoaderOnDeny: true,
      preConfirm: () => preConfirm({ prepareOnPreConfirm }),
      preDeny: () => preDeny({ prepareOnPreDeny }),
      confirmButtonText: (
        <span ref={confirmButtonTextRef}>{t('payment-card-modal.pay')}</span>
      ),
      denyButtonText: t('back'),
      didOpen,
      didDestroy,
    });

    if (wasSucccess) {
      result.isConfirmed = true;
      result.isDismissed = false;
    }

    result.wasOtherCardClicked = wasOtherCardClicked;
    result.wasDeleteCardClicked = wasDeleteCardClicked;
    result.cardToDelete = cardToDelete;

    return result;
  }

  async function preConfirm({ prepareOnPreConfirm }) {
    isUnderErrorRef.current = false;

    if (selectedCardRef.current == null) {
      const message = 'Selecione um cartão';
      swal.showValidationMessage(message);
      setSelectedErrorRef?.current?.call(this, message);

      return false;
    }

    setSelectedErrorRef?.current?.call(this, null);
    setLoading(true);

    try {
      await chargeRef.current();

      if (!isUnderErrorRef.current) {
        await refreshPaymentCard({
          payment: paymentRef.current,
          orders: ordersRef.current,
        });

        await prepareOnPreConfirm?.call(this);
        setLoading(false);
      }
    } catch (error) {
      handleErrorOnModal(error);
      setLoading(false);
      return false;
    }

    return true;
  }

  async function preDeny({ prepareOnPreDeny }) {
    try {
      await cancelPaymentCard(paymentRef.current);
      await prepareOnPreDeny?.call(this);
    } catch (error) {
      handleErrorOnModal(error);
      return false;
    }

    return true;
  }

  return {
    prepareSelectCardModal: prepare,
    showSelectCardModal: show,
  };
}

export { useSelectPaymentCardModal };
